import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ISubscriptionPlan } from '../../redux/subscription/types';
import { TransitionProps } from '@mui/material/transitions';
import { Box, Dialog, Slide, Typography, Button, Collapse, IconButton, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VerifiedIcon from '@mui/icons-material/Verified';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useThemeContext } from '../../hook/useThemeManager';
import { PaymentCardDropdownProps } from './PaymentCardDropdown';
import { IPaymentCard } from '../../types/global/generic.types';
import { userConfigurationSelector } from '../../selectors/selectors';
import { useSelector } from 'react-redux';
import { FONT_ENUM } from '../../types/global/fonts.types';
import PaymentCard from '../PaymentCard/PaymentCard';
import { SimpleAddCardModal } from '../Payment/SimpleAddCardModal';
import { PayStack } from '../Payment/Paystack';
import { isEmpty } from '../../utils/lodash';
import { getJWtDetails, showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import { initializeFlutterwaveSubscription, initializePaystackSubscription } from '../../redux/subscription/actions';
import { initializeFlutterwaveBillingCard } from '../../redux/payment/action';
import { splitFeatureTitleDetail } from './SubscriptionFeatureLabel';
import { PLAN_PAYMENT_MODAL_Z_INDEX } from '../../constants/subscriptionModalZIndex';
import {
    resolveCreatorTrialCtaLabel,
    resolveCreatorTrialDays,
    shouldRequestCreatorTrial,
} from '../../utils/creatorSubscriptionTrial';
import { en_config } from '../../config';
import { ROUTES } from '../../types/global/routes.types';
import { filterPaymentCardsForRail, resolveSubscriptionRail } from '../../utils/subscriptionRailCards';
import { stashCreatorSubscribeResume } from '../../utils/creatorSubscribeResume';
import { MIXPANEL_EVENT, trackSubscriptionAction } from '../../analytics/Mixpanel';

/** Midnight editorial tokens (aligned with design system HTML mock). */
const SUB = {
    primary: '#ffc965',
    primaryFixed: '#feb700',
    onPrimaryFixed: '#392700',
    secondary: '#00fdc1',
    surface: '#0e0e0e',
    surfaceContainerLow: '#131313',
    surfaceContainer: '#1a1919',
    surfaceVariant: '#262626',
    onSurface: '#ffffff',
    onSurfaceVariant: '#adaaaa',
    outline: '#767575',
    outlineVariant: '#484847',
};

interface IPlanAdditionModal extends Omit<PaymentCardDropdownProps, 'cards'> {
    open: boolean;
    userCards?: IPaymentCard[];
    plan?: ISubscriptionPlan;
    onClose: () => void;
    onProceedClick: (selectedCard?: IPaymentCard) => void;
    onCancelClick: () => void;
    /** After Paystack/Flutterwave saved-card subscribe succeeds (no redirect). */
    onHostedSubscribeSuccess?: () => void;
    isDowngrade?: boolean;
    isPromptMode?: boolean;
    isPaystack?: boolean;
    isFlutterwave?: boolean;
    paystackPublicKey?: string;
    paystackCallbackUrl?: string;
    /** After add-card redirect return, prefer the newest saved rail card. */
    preferNewestCard?: boolean;
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

function formatPlanPrice(plan: ISubscriptionPlan): string {
    if (plan.converted_amount) return plan.converted_amount.replace(/\/\w+$/, '').trim();
    return `$${(plan.amount / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Zero VAT in the same currency as the plan price line (not hard-coded ₦ / $). */
function formatZeroVat(planPriceLabel: string, isHostedCheckout: boolean): string {
    const match = planPriceLabel.match(/^([^\d\s.,]+)\s*/);
    const symbol = match?.[1]?.trim() || (isHostedCheckout ? '₦' : '$');
    const useDecimals = symbol === '$' || /\.\d/.test(planPriceLabel);
    return `${symbol}${useDecimals ? '0.00' : '0'}`;
}

function planIntervalLabel(interval: string): string {
    return interval === 'year' ? '/year' : '/month';
}

function savingsNote(plan: ISubscriptionPlan): string | null {
    if (plan.interval === 'year' && plan.original_price > plan.amount) {
        const pct = Math.round((1 - plan.amount / plan.original_price) * 100);
        if (pct > 0) return `Save ${pct}% with annual billing`;
    }
    if (plan.interval === 'year') return 'Save with annual billing';
    return null;
}

function featureHint(feature: string): string | null {
    const lower = feature.toLowerCase();
    if (lower.includes('free plan')) return 'Core features for your daily workflow';
    if (lower.includes('badge') || lower.includes('verified')) return 'Build trust within the creator community';
    if (lower.includes('application') || lower.includes('posting')) return 'No caps on how many projects you can join';
    if (lower.includes('saving') || lower.includes('campaign')) return 'Exclusive discounts on eligible campaigns';
    return null;
}

const PlanAdditionModal: React.FC<IPlanAdditionModal> = ({
    open,
    userCards,
    onClose,
    plan,
    onProceedClick,
    onCancelClick,
    onHostedSubscribeSuccess,
    isPromptMode,
    isPaystack,
    isFlutterwave,
    paystackPublicKey,
    paystackCallbackUrl,
    preferNewestCard,
}) => {
    const { device } = useThemeContext();
    const [selectedCard, setSelectedCard] = useState<IPaymentCard>();
    const [cardsExpanded, setCardsExpanded] = useState(false);
    const [isInitializing, setIsInitializing] = useState(false);
    const [flutterwaveAddingCard, setFlutterwaveAddingCard] = useState(false);
    const userSpecificConfiguration = useSelector(userConfigurationSelector);
    const dispatchPromise = useDispatchPromise();
    const { user } = getJWtDetails();
    const rail = resolveSubscriptionRail({ isFlutterwave, isPaystack });
    const isHostedCheckout = rail === 'paystack' || rail === 'flutterwave';
    const providerLabel = rail === 'flutterwave' ? 'Flutterwave' : 'Paystack';
    const railCards = useMemo(() => filterPaymentCardsForRail(userCards, rail), [userCards, rail]);

    const startHostedCheckout = async () => {
        if (!plan?.plan_code) return;
        if (rail === 'paystack' && !paystackPublicKey) return;
        const startTrial = shouldRequestCreatorTrial(plan.trial, { user });
        setIsInitializing(true);
        try {
            const initAction =
                rail === 'flutterwave' ? initializeFlutterwaveSubscription : initializePaystackSubscription;
            const res = (await dispatchPromise(
                initAction({
                    plan: plan.plan_code,
                    ...(paystackCallbackUrl ? { callback_url: paystackCallbackUrl } : {}),
                    ...(startTrial ? { start_trial: true } : {}),
                }),
            )) as any;
            const payload = res?.data ?? res;
            const accessCode = payload?.access_code;
            const authUrl = payload?.authorization_url;
            if (!accessCode && !authUrl) {
                showToastMessage(`Could not start ${providerLabel} checkout. Try again.`, 'error');
                return;
            }
            if (authUrl) {
                window.location.href = authUrl;
                return;
            }
            showToastMessage(`Could not open ${providerLabel} checkout URL.`, 'error');
        } catch (err: any) {
            showToastMessage(err?.data?.message ?? err?.message ?? `${providerLabel} checkout failed`, 'error');
        } finally {
            setIsInitializing(false);
        }
    };

    const startSavedCardSubscribe = async () => {
        if (!plan?.plan_code || !selectedCard?._id) return;
        const startTrial = shouldRequestCreatorTrial(plan.trial, { user });
        setIsInitializing(true);
        try {
            const initAction =
                rail === 'flutterwave' ? initializeFlutterwaveSubscription : initializePaystackSubscription;
            const res = (await dispatchPromise(
                initAction({
                    plan: plan.plan_code,
                    payment_method_id: selectedCard._id,
                    ...(paystackCallbackUrl ? { callback_url: paystackCallbackUrl } : {}),
                    ...(startTrial ? { start_trial: true } : {}),
                }),
            )) as any;
            const payload = res?.data ?? res;
            // 3DS / hosted fallback — redirect before treating as complete
            const authUrl = payload?.authorization_url || payload?.payment_link;
            if (authUrl && payload?.verified !== true) {
                window.location.href = authUrl;
                return;
            }
            if (payload?.verified || payload?.checkout_mode === 'saved_card' || payload?.subscription) {
                showToastMessage(
                    startTrial ? 'Trial started with your saved card' : 'Subscription activated',
                    'success',
                );
                onHostedSubscribeSuccess?.();
                return;
            }
            if (authUrl) {
                window.location.href = authUrl;
                return;
            }
            showToastMessage(`Could not complete ${providerLabel} subscription. Try again.`, 'error');
        } catch (err: any) {
            showToastMessage(err?.data?.message ?? err?.message ?? `${providerLabel} subscription failed`, 'error');
        } finally {
            setIsInitializing(false);
        }
    };

    const handleFlutterwaveAddCard = async (e?: React.MouseEvent) => {
        e?.preventDefault();
        e?.stopPropagation();
        if (!plan?.plan_code) return;
        setFlutterwaveAddingCard(true);
        try {
            stashCreatorSubscribeResume(plan.plan_code);
            const res = (await dispatchPromise(
                initializeFlutterwaveBillingCard({
                    callback_url: `${window.location.origin}${ROUTES.ACCOUNT_SUBSCRIPTION}`,
                }),
            )) as any;
            const payload = res?.data ?? res;
            const authUrl = payload?.authorization_url || payload?.payment_link;
            if (!authUrl) {
                showToastMessage('Could not start Flutterwave card check. Try again.', 'error');
                return;
            }
            window.location.href = authUrl;
        } catch {
            showToastMessage('Could not start Flutterwave card check. Try again.', 'error');
        } finally {
            setFlutterwaveAddingCard(false);
        }
    };

    const handleSetDefaultCard = useCallback(() => {
        if (!railCards.length) {
            setSelectedCard(undefined);
            return;
        }
        const newest = [...railCards].sort(
            (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime(),
        )[0];
        // Keep selecting newest while resume flag is on (cards may arrive after modal opens).
        if (preferNewestCard && newest) {
            setSelectedCard(newest);
            return;
        }
        const defaultCard = railCards.find(card => card._id === userSpecificConfiguration?.default_payment_method);
        setSelectedCard(defaultCard ?? newest);
    }, [railCards, userSpecificConfiguration?.default_payment_method, preferNewestCard]);

    useEffect(() => {
        handleSetDefaultCard();
    }, [handleSetDefaultCard]);

    const startTrial = shouldRequestCreatorTrial(plan?.trial, { user });
    const trialDays = resolveCreatorTrialDays(plan?.trial);
    const tokenizeMinor = plan?.trial?.tokenize_amount_kobo ?? 5000;
    const tokenizeCurrency = (plan?.trial?.tokenize_currency || 'NGN').toUpperCase();
    const tokenizeSymbol =
        tokenizeCurrency === 'NGN'
            ? '₦'
            : tokenizeCurrency === 'GHS'
            ? 'GH₵'
            : tokenizeCurrency === 'KES'
            ? 'KSh'
            : tokenizeCurrency === 'ZAR'
            ? 'R'
            : tokenizeCurrency === 'USD'
            ? '$'
            : `${tokenizeCurrency} `;
    const tokenizeLabel = `${tokenizeSymbol}${(tokenizeMinor / 100).toLocaleString(undefined, {
        minimumFractionDigits: tokenizeCurrency === 'USD' ? 2 : 0,
        maximumFractionDigits: 2,
    })}`;
    const planPriceLabel = plan ? formatPlanPrice(plan) : '—';
    const zeroVatLabel = formatZeroVat(planPriceLabel === '—' ? '' : planPriceLabel, isHostedCheckout);
    const usingSavedCard = isHostedCheckout && Boolean(selectedCard);
    const dueTodayLabel = useMemo(() => {
        if (!plan) return '—';
        if (startTrial && usingSavedCard) return `${tokenizeSymbol}0`;
        if (startTrial && isHostedCheckout) {
            return tokenizeLabel;
        }
        if (startTrial) return '$0.00';
        return planPriceLabel;
    }, [plan, startTrial, isHostedCheckout, usingSavedCard, tokenizeLabel, tokenizeSymbol, planPriceLabel]);
    const saveLine = useMemo(() => {
        if (startTrial) {
            if (usingSavedCard) {
                return `No charge today — ${trialDays} days free, then ${planPriceLabel}${
                    plan ? planIntervalLabel(plan.interval) : ''
                }`;
            }
            return isHostedCheckout
                ? `${tokenizeLabel} card check — refunded. Then ${trialDays} days free.`
                : `No charge today — ${trialDays}-day free trial, then ${planPriceLabel}${
                      plan ? planIntervalLabel(plan.interval) : ''
                  }`;
        }
        return plan ? savingsNote(plan) : null;
    }, [plan, startTrial, isHostedCheckout, usingSavedCard, tokenizeLabel, trialDays, planPriceLabel]);

    if (isPromptMode && plan && plan.amount === 0) {
        return null;
    }

    const requiresPayment = plan && plan.amount > 0;
    const canConfirmSaved = Boolean(selectedCard) && !isInitializing;
    const canConfirmHosted = !isInitializing && (rail === 'flutterwave' || Boolean(paystackPublicKey));
    const canConfirm = isHostedCheckout
        ? canConfirmSaved || canConfirmHosted
        : !requiresPayment || Boolean(selectedCard);
    const confirmLabel = (() => {
        if (isInitializing) {
            return usingSavedCard ? 'Confirming…' : `Opening ${providerLabel}…`;
        }
        if (isHostedCheckout && selectedCard) {
            return startTrial ? resolveCreatorTrialCtaLabel(plan?.trial) : 'Confirm with saved card';
        }
        if (isHostedCheckout) {
            return startTrial ? resolveCreatorTrialCtaLabel(plan?.trial) : `Pay with ${providerLabel}`;
        }
        return startTrial ? resolveCreatorTrialCtaLabel(plan?.trial) : 'Confirm purchase';
    })();
    const headerBadge = startTrial ? 'Free trial' : 'Premium upgrade';
    const stayLabel = startTrial || isPromptMode ? 'Stay as I am' : 'Cancel';
    const legalNote = startTrial
        ? usingSavedCard
            ? `Your saved card stays on file but you won't be charged until the ${trialDays}-day trial ends. Cancel anytime before then to avoid billing for ${
                  plan?.name ?? 'this plan'
              } (${planPriceLabel}${plan ? planIntervalLabel(plan.interval) : ''}).`
            : isHostedCheckout
            ? `We'll charge ${tokenizeLabel} to verify your card and refund it right away. Your ${trialDays}-day trial starts immediately; billing for ${
                  plan?.name ?? 'this plan'
              } (${planPriceLabel}${
                  plan ? planIntervalLabel(plan.interval) : ''
              }) begins after the trial unless you cancel.`
            : `Your card is saved now but you won't be charged until the ${trialDays}-day trial ends. Cancel anytime before then to avoid billing.`
        : `By confirming, you authorize Thspian to charge your payment method for the amount above plus applicable taxes. Subscription automatically renews each ${plan?.interval} unless canceled.`;

    const renderHostedAddCard = () => {
        if (rail === 'paystack' && user && paystackPublicKey) {
            return (
                <PayStack
                    email={user.email}
                    amount={en_config.PAYSTACK_CHARGE}
                    publicKey={paystackPublicKey}
                    isAddNewCardButton={false}
                    addButtonSx={{
                        border: 'none',
                        bgcolor: 'transparent',
                        color: SUB.primary,
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 13,
                        px: 1,
                        py: 0.5,
                        minWidth: 0,
                        '&:hover': { bgcolor: 'transparent', color: SUB.onSurface },
                    }}
                    addButtonTextStyle={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 13,
                        color: SUB.primary,
                    }}
                    addButtonProps={{ variant: 'text' }}
                />
            );
        }
        if (rail === 'flutterwave') {
            return (
                <Button
                    type="button"
                    onClick={e => void handleFlutterwaveAddCard(e)}
                    disabled={flutterwaveAddingCard}
                    sx={{
                        border: 'none',
                        bgcolor: 'transparent',
                        color: SUB.primary,
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 13,
                        px: 1,
                        py: 0.5,
                        minWidth: 0,
                        '&:hover': { bgcolor: 'transparent', color: SUB.onSurface },
                    }}>
                    {flutterwaveAddingCard ? <CircularProgress size={14} sx={{ color: SUB.primary, mr: 1 }} /> : null}
                    {flutterwaveAddingCard ? 'Opening…' : 'Add card'}
                </Button>
            );
        }
        return null;
    };

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={onClose}
            fullWidth
            maxWidth="xs"
            PaperProps={{
                sx: {
                    bgcolor: SUB.surface,
                    backgroundImage: 'none',
                    border: `1px solid ${SUB.outlineVariant}`,
                    borderRadius: '12px',
                    width: device.isMobile ? '92%' : '100%',
                    maxWidth: 448,
                    mx: 'auto',
                    overflow: 'hidden',
                },
            }}
            sx={{ zIndex: PLAN_PAYMENT_MODAL_Z_INDEX }}
            style={{ zIndex: PLAN_PAYMENT_MODAL_Z_INDEX }}
            aria-describedby="plan-addition-dialog">
            <Box sx={{ px: { xs: 2.5, sm: 3 }, pt: 3, pb: 3, maxHeight: '90vh', overflowY: 'auto' }}>
                {/* Header */}
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Box
                        sx={{
                            display: 'inline-block',
                            px: 2,
                            py: 0.75,
                            mb: 2,
                            borderRadius: 999,
                            bgcolor: `${SUB.primaryFixed}33`,
                            color: SUB.primary,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 10,
                            letterSpacing: '0.14em',
                            textTransform: 'uppercase',
                        }}>
                        {headerBadge}
                    </Box>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: { xs: 28, sm: 34 },
                            fontWeight: 800,
                            color: SUB.onSurface,
                            lineHeight: 1.15,
                            mb: 1,
                        }}>
                        {plan?.name ?? 'Subscription'}
                    </Typography>
                    {plan ? (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.5 }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 36,
                                        fontWeight: 800,
                                        color: SUB.primary,
                                    }}>
                                    {planPriceLabel}
                                </Typography>
                                <Typography sx={{ fontSize: 14, color: SUB.onSurfaceVariant }}>
                                    {planIntervalLabel(plan.interval)}
                                </Typography>
                            </Box>
                            {saveLine ? (
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        color: SUB.secondary,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        mt: 0.5,
                                    }}>
                                    {saveLine}
                                </Typography>
                            ) : null}
                        </>
                    ) : null}
                </Box>

                {/* Perks */}
                {plan?.features?.length ? (
                    <Box
                        sx={{
                            bgcolor: SUB.surfaceContainerLow,
                            borderRadius: '12px',
                            p: 3,
                            mb: 3,
                        }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 18,
                                color: SUB.onSurface,
                                mb: 2.5,
                            }}>
                            Subscription perks
                        </Typography>
                        <Box
                            component="ul"
                            sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            {plan.features.map((feature, index) => {
                                const { title, detail } = splitFeatureTitleDetail(feature);
                                const hint = detail || featureHint(feature);
                                const showVerified = /badge|verified/i.test(feature);
                                return (
                                    <Box
                                        component="li"
                                        key={index}
                                        sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                                        <Box
                                            sx={{
                                                mt: 0.25,
                                                bgcolor: `${SUB.primary}1a`,
                                                p: 0.5,
                                                borderRadius: 1,
                                                flexShrink: 0,
                                            }}>
                                            <CheckCircleIcon sx={{ fontSize: 20, color: SUB.primary }} />
                                        </Box>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.75,
                                                    flexWrap: 'wrap',
                                                }}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        fontSize: 15,
                                                        color: SUB.onSurface,
                                                    }}>
                                                    {title}
                                                </Typography>
                                                {showVerified ? (
                                                    <VerifiedIcon sx={{ fontSize: 16, color: SUB.secondary }} />
                                                ) : null}
                                            </Box>
                                            {hint ? (
                                                <Typography
                                                    sx={{
                                                        fontSize: 13,
                                                        color: SUB.onSurfaceVariant,
                                                        mt: 0.25,
                                                        lineHeight: 1.45,
                                                    }}>
                                                    {hint}
                                                </Typography>
                                            ) : null}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                ) : null}

                {/* Payment — Stripe or Paystack/Flutterwave saved cards */}
                {requiresPayment ? (
                    <Box sx={{ mb: 3 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 1.5,
                                px: 0.5,
                            }}>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 18, color: SUB.onSurface }}>
                                Payment method
                            </Typography>
                            {isHostedCheckout ? renderHostedAddCard() : <SimpleAddCardModal variant="link" />}
                        </Box>
                        <Box sx={{ position: 'relative' }}>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: -2,
                                    borderRadius: '12px',
                                    background: `linear-gradient(90deg, ${SUB.primary}33, ${SUB.secondary}33)`,
                                    opacity: 0.35,
                                    filter: 'blur(8px)',
                                    pointerEvents: 'none',
                                }}
                            />
                            <Box
                                onClick={() => railCards.length && setCardsExpanded(v => !v)}
                                sx={{
                                    position: 'relative',
                                    bgcolor: 'rgba(38, 38, 38, 0.6)',
                                    backdropFilter: 'blur(20px)',
                                    border: '1px solid rgba(255,255,255,0.06)',
                                    borderRadius: '12px',
                                    p: 2.5,
                                    cursor: railCards.length ? 'pointer' : 'default',
                                }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                    }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        {selectedCard ? (
                                            <PaymentCard card={selectedCard} isModalMode cardSize="30px" isSelected />
                                        ) : (
                                            <Typography sx={{ fontSize: 13, color: SUB.onSurfaceVariant }}>
                                                {isEmpty(railCards)
                                                    ? isHostedCheckout
                                                        ? `No ${providerLabel} card on file — add a card or checkout on ${providerLabel}`
                                                        : 'No card on file — add a card to continue'
                                                    : 'Select a card'}
                                            </Typography>
                                        )}
                                    </Box>
                                    {railCards.length ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                            <Box
                                                sx={{
                                                    width: 22,
                                                    height: 22,
                                                    borderRadius: '50%',
                                                    border: `2px solid ${SUB.primary}`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}>
                                                {selectedCard ? (
                                                    <Box
                                                        sx={{
                                                            width: 10,
                                                            height: 10,
                                                            borderRadius: '50%',
                                                            bgcolor: SUB.primary,
                                                        }}
                                                    />
                                                ) : null}
                                            </Box>
                                            <IconButton size="small" sx={{ color: SUB.primary }}>
                                                {cardsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </IconButton>
                                        </Box>
                                    ) : null}
                                </Box>
                                <Collapse in={cardsExpanded}>
                                    <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${SUB.outlineVariant}` }}>
                                        {railCards.map(card => (
                                            <Box
                                                key={card._id}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    setSelectedCard(card);
                                                    setCardsExpanded(false);
                                                }}
                                                sx={{
                                                    cursor: 'pointer',
                                                    mb: 1,
                                                    borderRadius: 2,
                                                    '&:hover': { bgcolor: SUB.surfaceVariant },
                                                }}>
                                                <PaymentCard
                                                    card={card}
                                                    isSelected={selectedCard?._id === card._id}
                                                    isModalMode
                                                    cardSize="30px"
                                                />
                                            </Box>
                                        ))}
                                    </Box>
                                </Collapse>
                            </Box>
                        </Box>
                    </Box>
                ) : null}

                {/* Summary */}
                {plan && requiresPayment ? (
                    <Box sx={{ mb: 2, px: 0.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography sx={{ fontSize: 13, color: SUB.onSurfaceVariant }}>Plan price</Typography>
                            <Typography sx={{ fontSize: 13, color: SUB.onSurfaceVariant }}>
                                {planPriceLabel}
                                {planIntervalLabel(plan.interval)}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography sx={{ fontSize: 13, color: SUB.onSurfaceVariant }}>VAT (0%)</Typography>
                            <Typography sx={{ fontSize: 13, color: SUB.onSurfaceVariant }}>{zeroVatLabel}</Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                pt: 2,
                                mt: 1,
                                borderTop: `1px solid ${SUB.surfaceVariant}`,
                            }}>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 17, color: SUB.onSurface }}>
                                {startTrial
                                    ? usingSavedCard || !isHostedCheckout
                                        ? 'Due today'
                                        : 'Card check'
                                    : 'Total charge'}
                            </Typography>
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 20, color: SUB.primary }}>
                                {dueTodayLabel}
                            </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 10, color: `${SUB.onSurfaceVariant}99`, lineHeight: 1.55, mt: 2 }}>
                            {legalNote}
                        </Typography>
                    </Box>
                ) : null}

                {/* Actions */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                    <Button
                        fullWidth
                        disabled={
                            isHostedCheckout ? (selectedCard ? !canConfirmSaved : !canConfirmHosted) : !canConfirm
                        }
                        onClick={() => {
                            trackSubscriptionAction(
                                startTrial ? MIXPANEL_EVENT.START_TRIAL_CLICK : MIXPANEL_EVENT.SUBSCRIBE_CLICK,
                                {
                                    buttonName: String(confirmLabel).toUpperCase(),
                                    buttonAction: startTrial ? 'START_TRIAL' : 'SUBSCRIBE',
                                    subscriptionType: 'creator',
                                    planCode: plan?.plan_code ?? plan?.plan?.id ?? null,
                                    planName: plan?.plan_name ?? plan?.name ?? null,
                                    startTrial,
                                },
                            );
                            if (!isHostedCheckout) {
                                onProceedClick(selectedCard);
                                return;
                            }
                            if (selectedCard) {
                                void startSavedCardSubscribe();
                                return;
                            }
                            void startHostedCheckout();
                        }}
                        sx={{
                            py: 1.75,
                            borderRadius: 999,
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 16,
                            fontWeight: 800,
                            color: SUB.onPrimaryFixed,
                            background: `linear-gradient(135deg, ${SUB.primary} 0%, ${SUB.primaryFixed} 100%)`,
                            boxShadow: `0 10px 30px ${SUB.primaryFixed}4d`,
                            '&:hover': {
                                background: `linear-gradient(135deg, ${SUB.primary} 0%, ${SUB.primaryFixed} 100%)`,
                                filter: 'brightness(1.05)',
                            },
                            '&.Mui-disabled': {
                                opacity: 0.45,
                                color: SUB.onPrimaryFixed,
                            },
                        }}>
                        {confirmLabel}
                    </Button>
                    {isHostedCheckout && selectedCard ? (
                        <Button
                            fullWidth
                            disabled={!canConfirmHosted}
                            onClick={() => void startHostedCheckout()}
                            sx={{
                                py: 1.25,
                                borderRadius: 999,
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 14,
                                color: SUB.secondary,
                                border: `1px solid ${SUB.outlineVariant}`,
                                '&:hover': { bgcolor: SUB.surfaceVariant, color: SUB.onSurface },
                            }}>
                            {isInitializing ? `Opening ${providerLabel}…` : `Checkout on ${providerLabel}`}
                        </Button>
                    ) : null}
                    <Button
                        fullWidth
                        onClick={() => {
                            trackSubscriptionAction(MIXPANEL_EVENT.DISMISS_SUBSCRIPTION_MODAL_CLICK, {
                                buttonName: String(stayLabel).toUpperCase(),
                                buttonAction: 'DISMISS_SUBSCRIPTION_MODAL',
                                subscriptionType: 'creator',
                                startTrial,
                            });
                            onCancelClick();
                        }}
                        sx={{
                            py: 1.25,
                            borderRadius: 999,
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 14,
                            color: SUB.onSurfaceVariant,
                            '&:hover': { bgcolor: 'transparent', color: SUB.onSurface },
                        }}>
                        {stayLabel}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};

export { PlanAdditionModal };
